import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, TextArea } from '~/components/ui';
import { useGetQuoteComments, usePostQuoteComment } from '~/api';
import { formatDateTime, sellerLabel, toastGraphQLError } from '~/utils';

export const COMMENT_MAX_LENGTH = 2000;

export type QuoteConversationDrafts = Map<string, string>;

type QuoteConversationProps = Readonly<{
  quoteId: string;
  canComment: boolean;
  /** Keeps unsent text by quote id after the composer unmounts. */
  drafts?: QuoteConversationDrafts;
}>;

export function QuoteConversation({
  quoteId,
  canComment,
  drafts,
}: QuoteConversationProps) {
  const { t } = useTranslation('rfq-view');
  const { data, isLoading, isError } = useGetQuoteComments(quoteId);
  const { mutateAsync: postComment, isPending } = usePostQuoteComment();
  const [body, setBodyState] = useState(() => drafts?.get(quoteId) ?? '');

  const setBody = (value: string) => {
    setBodyState(value);
    drafts?.set(quoteId, value);
  };
  const comments = data?.quoteComments ?? [];
  const canPost = body.trim().length > 0 && !isPending;

  const handlePost = async () => {
    try {
      await postComment({ quoteId, body: body.trim() });
      setBody('');
    } catch (error) {
      toastGraphQLError(error);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <h4 className="text-sm font-semibold text-neut-prim">
        {t('rfq_view.conversation_heading')}
      </h4>

      {isError && (
        <p className="text-sm text-error-600" role="alert">
          {t('rfq_view.conversation_load_failed')}
        </p>
      )}

      {!isError && !isLoading && comments.length === 0 && (
        <p className="text-sm text-neut-sec">
          {t('rfq_view.conversation_empty')}
        </p>
      )}

      {comments.map((comment) => (
        <div key={comment.id} className="flex flex-col gap-0.5">
          <p
            className="text-xs text-neut-sec"
            data-testid="quote-comment-byline"
          >
            {[
              comment.authorName,
              sellerLabel(comment.authorOrganization),
              formatDateTime(comment.createdAt),
            ]
              .filter((part) => part.length > 0)
              .join(' · ')}
          </p>
          <p
            className="text-sm text-neut-prim"
            data-testid="quote-comment-body"
          >
            {comment.body}
          </p>
        </div>
      ))}

      {!isError && canComment && (
        <>
          <TextArea
            id={`${quoteId}-comment`}
            name={`${quoteId}-comment`}
            label={t('rfq_view.conversation_label')}
            placeholder={t('rfq_view.conversation_placeholder')}
            rows={3}
            maxLength={COMMENT_MAX_LENGTH}
            value={body}
            disabled={isPending}
            onChange={(_, value) => setBody(value)}
          />

          <div className="flex justify-end">
            <Button
              variant="primary"
              size="sm"
              width="auto"
              onClick={() => void handlePost()}
              disabled={!canPost}
              loading={isPending}
            >
              {t('rfq_view.conversation_post')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
